// EstudiantesReportPage.jsx - Vista de estudiantes con estilo de reportes
import React, { useEffect, useState, useMemo } from 'react';
import { getSecurityUsers } from '../../Lms/Stores/securityUsersStore';
import { normalizeValue } from '../../../utils/Texts';
import './../../../style/Vars.css';
import './../../../style/ContainersStyles.css';
import './../../../style/ElementsStyle.css';
import AlertDialog from '../Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Estudiantes Registrados";

export default function EstudiantesReportPage() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [roleFilter, setRoleFilter] = useState('all');

    // Estado para modal de alerta
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

    // Cargar datos
    const loadData = async () => {
        setLoading(true);
        try {
            // Obtener usuarios del store (en el futuro será desde API)
            const users = getSecurityUsers();
            setRows(users);
        } catch (error) {
            console.error('Error al cargar estudiantes:', error);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'Inténtelo de nuevo más tarde. Gracias',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtrado
    const filtered = useMemo(() => {
        let data = rows;

        // Filtro de búsqueda
        if (query.trim()) {
            const q = normalizeValue(query);
            data = data.filter(u => {
                const vals = [
                    u.username,
                    u.nombreCompleto,
                    u.email,
                    u.area,
                    u.puesto
                ].map(v => normalizeValue(v || ''));
                return vals.some(v => v.includes(q));
            });
        }

        // Filtro de rol
        if (roleFilter !== 'all') {
            data = data.filter(u => u.rol === roleFilter);
        }

        return data;
    }, [rows, query, roleFilter]);

    // Paginación
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    // Exportar a Excel (CSV)
    const downloadExcel = () => {
        if (!rows || rows.length === 0) {
            setAlertDialog({
                open: true,
                title: 'Información',
                message: 'No hay datos para exportar.',
                variant: 'info'
            });
            return;
        }

        const headers = [
            "Usuario", "Nombre Completo", "Correo", "Rol",
            "Área", "Puesto", "Estado", "Última Conexión"
        ];

        const csvRows = [
            headers.join(","),
            ...rows.map(row => {
                return [
                    `"${row.username || ''}"`,
                    `"${row.nombreCompleto || ''}"`,
                    `"${row.email || ''}"`,
                    `"${row.rol || ''}"`,
                    `"${row.area || ''}"`,
                    `"${row.puesto || ''}"`,
                    `"${row.estado || ''}"`,
                    `"${row.ultimaConexion || ''}"`
                ].join(",");
            })
        ];

        const csvString = "\uFEFF" + csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Estudiantes_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por usuario, nombre, correo..."
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                    />

                    <select
                        className="roles-select"
                        value={roleFilter}
                        onChange={(e) => {
                            setRoleFilter(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="all">Todos los roles</option>
                        <option value="Administrador">Administrador</option>
                        <option value="Supervisor">Supervisor</option>
                        <option value="Instructor">Instructor</option>
                        <option value="Usuario">Usuario</option>
                        <option value="Auditor">Auditor</option>
                    </select>

                    <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel">
                        📥 Descargar Excel
                    </button>
                </div>

                {loading ? (
                    <div className="catalog-loading">Cargando estudiantes...</div>
                ) : (
                    <div className="roles-table-wrap">
                        <div className="roles-table-scroller">
                            <table className="roles-table">
                                <thead>
                                    <tr>
                                        <th>Usuario</th>
                                        <th>Nombre Completo</th>
                                        <th>Correo</th>
                                        <th>Rol</th>
                                        <th>Área</th>
                                        <th>Puesto</th>
                                        <th>Estado</th>
                                        <th>Última Conexión</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: 'center' }}>
                                                No se encontraron estudiantes.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginated.map((user, index) => (
                                            <tr key={user.id || index}>
                                                <td>
                                                    <div className="sec-user-cell">
                                                        <div className="sec-user-avatar">
                                                            {user.nombreCompleto.charAt(0)}
                                                        </div>
                                                        <div className="sec-user-username">{user.username}</div>
                                                    </div>
                                                </td>
                                                <td>{user.nombreCompleto || 'N/A'}</td>
                                                <td>{user.email || 'N/A'}</td>
                                                <td>
                                                    <span className="sec-user-role">{user.rol || 'N/A'}</span>
                                                </td>
                                                <td>{user.area || 'N/A'}</td>
                                                <td>{user.puesto || 'N/A'}</td>
                                                <td>
                                                    <span className={`sec-user-status ${user.estado === 'Inactivo' ? 'inactive' : ''}`}>
                                                        {user.estado || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{user.ultimaConexion || 'N/A'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>
            </div>

            {/* Modal de alerta */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />
        </div>
    );
}
